import React from 'react';
import { useSpinradContext } from '../../context/SpinradContext';
import { dimOrder, EB_LOGO_URL, theme } from '../../data';
import { DimensionId } from '../../types';
import {
  InfoDot,
  InfoPop,
  Panel,
  SaHint,
  SaWork,
  SbInfo,
  SpinActions,
  SpinButton,
  SpinDup,
  SpinDupBtn,
  SpinDupTxt,
  TodayQ,
  TqFrom,
  TqLbl,
  TqTxt,
  WheelPointer,
  WheelSvg,
  WheelWrap,
} from './styles';

const CX = 90;
const CY = 90;
const R = 76;
const hubR = 18;
const segAngle = 360 / dimOrder.length;

const labelLinesByDim: Record<DimensionId, string[]> = {
  ZUSAMMENARBEIT: ['ZUSAMMEN-', 'ARBEIT'],
  HANDELN: ['HANDELN'],
  SEIN: ['SEIN'],
  DENKEN: ['DENKEN'],
  BEZIEHUNG: ['BEZIEHUNG'],
};

interface WheelSegment {
  id: DimensionId;
  path: string;
  color: string;
  labelLines: string[];
  lx: number;
  ly: number;
}

const toRad = (deg: number): number => (deg * Math.PI) / 180;

const wheelSegments: WheelSegment[] = dimOrder.map((id, i) => {
  const th = theme(id);
  const a0 = toRad(-90 + i * segAngle);
  const a1 = toRad(-90 + (i + 1) * segAngle);
  const x0 = CX + R * Math.cos(a0);
  const y0 = CY + R * Math.sin(a0);
  const x1 = CX + R * Math.cos(a1);
  const y1 = CY + R * Math.sin(a1);
  const mid = (a0 + a1) / 2;
  const lr = R * 0.62;
  return {
    id,
    path: `M${CX},${CY} L${x0.toFixed(2)},${y0.toFixed(2)} A${R},${R} 0 0 1 ${x1.toFixed(2)},${y1.toFixed(2)} Z`,
    color: th.color,
    labelLines: labelLinesByDim[id],
    lx: CX + lr * Math.cos(mid),
    ly: CY + lr * Math.sin(mid),
  };
});

const Wheel: React.FC = () => {
  const {
    pendingSpin,
    wheelRotation,
    spinning,
    spin,
    findUsage,
    handleSpinDiscuss,
    openModal,
  } = useSpinradContext();

  const revealed = !spinning && !!pendingSpin;
  const revealedTheme = pendingSpin ? theme(pendingSpin.from) : null;

  const bg = revealed && revealedTheme ? revealedTheme.color : '#F3EDE6';
  const textColor = revealed && revealedTheme ? revealedTheme.ink : '#3a2a1a';
  const labelColor =
    revealed && revealedTheme
      ? revealedTheme.ink === '#ffffff'
        ? 'rgba(255,255,255,.85)'
        : revealedTheme.accent
      : '#9a7a55';

  const questionText =
    revealed && pendingSpin ? pendingSpin.q : 'Dreh das Rad, um zu starten.';
  const fromText =
    revealed && revealedTheme ? `Gezogen aus: ${revealedTheme.name}` : '';

  const usage = revealed && pendingSpin ? findUsage(pendingSpin.q) : null;

  const btnLabel = spinning
    ? 'Dreht…'
    : pendingSpin
      ? 'Nochmal drehen'
      : 'Dreh das Rad';

  let dupMsg = '';
  if (usage) {
    dupMsg =
      usage.total === 1
        ? usage.where === 'gerade in Arbeit'
          ? 'Diese Aussage ist gerade in Arbeit.'
          : 'Diese Aussage wurde bereits bearbeitet.'
        : `Diese Aussage wurde bereits ${usage.total}× verwendet · zuletzt ${usage.where}.`;
  }

  return (
    <Panel>
      <SbInfo>
        <InfoDot>i</InfoDot>
        <InfoPop>
          <h3>Das IDG-Rad</h3>
          <p>
            Die fünf Dimensionen – Sein, Denken, Beziehung, Zusammenarbeit,
            Handeln – werden spielerisch erlebbar. Jede Drehung zeigt eine von
            25 Team-Aussagen. Trifft das auf euer Team zu? Wo seht ihr Stärken,
            wo Potenzial?
          </p>
        </InfoPop>
      </SbInfo>

      <WheelWrap>
        <WheelPointer />
        <WheelSvg
          viewBox="0 0 180 180"
          style={{ transform: `rotate(${wheelRotation}deg)` }}
        >
          <defs>
            <clipPath id="ebHubClip">
              <circle cx={CX} cy={CY} r={hubR} />
            </clipPath>
          </defs>
          {wheelSegments.map(seg => (
            <g key={seg.id}>
              <path
                d={seg.path}
                fill={seg.color}
                stroke="#fff"
                strokeWidth={1}
              />
              {seg.labelLines.map((ln, j) => (
                <text
                  key={ln}
                  x={seg.lx}
                  y={seg.ly + (j - (seg.labelLines.length - 1) / 2) * 8}
                  textAnchor="middle"
                  fontSize={7}
                  fontWeight={700}
                  fill="#000000"
                  fontFamily="Open Sans, sans-serif"
                >
                  {ln}
                </text>
              ))}
            </g>
          ))}
          <circle cx={CX} cy={CY} r={hubR} fill="#ffffff" />
          <image
            x={CX - (hubR + 3)}
            y={CY - (hubR + 3)}
            width={hubR * 2 + 6}
            height={hubR * 2 + 6}
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#ebHubClip)"
            href={EB_LOGO_URL}
          />
        </WheelSvg>
      </WheelWrap>

      <SpinButton type="button" onClick={spin} disabled={spinning}>
        {btnLabel}
      </SpinButton>

      <TodayQ $bg={bg}>
        <TqLbl $color={labelColor}>Gedrehte Frage</TqLbl>
        <TqTxt $color={textColor}>{questionText}</TqTxt>
        <TqFrom $color={labelColor}>{fromText}</TqFrom>
      </TodayQ>

      {usage && (
        <SpinDup>
          <SpinDupTxt>⚠️ {dupMsg}</SpinDupTxt>
          <SpinDupBtn type="button" onClick={() => openModal(usage.latest.id)}>
            Karte ansehen
          </SpinDupBtn>
        </SpinDup>
      )}

      {revealed && (
        <SpinActions>
          <SaWork type="button" onClick={handleSpinDiscuss}>
            Frage diskutieren
          </SaWork>
          <SaHint>Oder erneut drehen für eine andere Frage.</SaHint>
        </SpinActions>
      )}
    </Panel>
  );
};

export default Wheel;
